<template>
    <div>
        <div id="recaptcha-container"></div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';

declare var grecaptcha: any;
declare var window: any;
const reCaptchaSiteKeyV2 = '6LcAc80rAAAAAJitWTRGndpldFfB7BCiBvppvRkD';
const reCaptchaSiteKeyV3 = '6LfkOwEpAAAAAAOkxAP_xKw4VtT253hx7VtTZsNB';

const props = defineProps({
    action: { type: String, default: 'submit' }, // only for v3
    hl: { type: String, default: 'en' },
    version: { type: String, default: 'v3' }, // use ReCaptcha v2 or v3
    autoLoad: { type: Boolean, default: true }, // only for version: v2
    autoExecute: { type: Boolean, default: true } // only for version: v3
});

const emit = defineEmits(['verify', 'error', 'expired']);

const loaded = ref(false);
const widgetId = ref();

const siteKey = computed((): string => {
    return props.version === 'v2' ? reCaptchaSiteKeyV2 : reCaptchaSiteKeyV3;
});

function loadRecaptchaScript() {
    return new Promise((resolve, reject) => {
        if (window.grecaptcha) return resolve(undefined);
        const script = document.createElement('script');
        script.src =
            props.version === 'v2'
                ? `https://www.google.com/recaptcha/api.js?hl=${props.hl}`
                : `https://www.google.com/recaptcha/api.js?render=${siteKey.value}&hl=${props.hl}`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(undefined);
        script.onerror = () => reject('Failed to load reCAPTCHA script.');
        document.head.appendChild(script);
    });
}

function renderV2() {
    if (!window.grecaptcha) {
        emit('error', 'reCAPTCHA library not loaded.');
        return;
    }
    try {
        window.grecaptcha.render('recaptcha-container', {
            sitekey: siteKey.value,
            callback: (token) => emit('verify', token),
            'expired-callback': () => emit('expired'),
            'error-callback': (error) =>
                emit('error', error || 'Unknown reCAPTCHA v2 error')
        });
    } catch (err: any) {
        emit('error', `Error during reCAPTCHA v2 rendering: ${err?.message}`);
    }
}

function renderWait() {
    setTimeout(() => {
        if (typeof grecaptcha !== 'undefined' && typeof grecaptcha.render !== 'undefined') renderV2();
        else renderWait();
    }, 200);
}

function render() {
    const recaptchaDiv = document.createElement('div');
    recaptchaDiv.className = 'outside-badge';
    document.body.appendChild(recaptchaDiv);

    widgetId.value = grecaptcha.render(recaptchaDiv, {
        sitekey: siteKey.value,
        callback: token => {
            grecaptcha.reset(widgetId.value);
        },
        'expired-callback': () => emit('expired'),
        'error-callback': (error: any) =>
            emit('error', error || 'Unknown reCAPTCHA v2 error')
    });
    loaded.value = true;
}

async function executeRecaptcha() {
    try {
        await loadRecaptchaScript();
        if (props.version === 'v2') {
            renderV2();
        } else {
            window.grecaptcha.ready(() => {
                window.grecaptcha
                    .execute(siteKey.value, { action: props.action })
                    .then((token) => emit('verify', token))
                    .catch((err) => emit('error', err));
            });
        }
    } catch (err) {
        emit('error', err);
    }
}

defineExpose({
    execute: executeRecaptcha
});

onMounted(async() => {
    if (props.version === 'v2' && props.autoLoad) {
        await loadRecaptchaScript();
        // renderV2();
        renderWait();
    } else if (props.version === 'v3' && props.autoExecute) {
        executeRecaptcha();
    }
});
</script>
